import React, { useCallback, useState } from 'react';
import { Bot } from 'lucide-react';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { AiChatPanel } from './AiChatPanel';
import { TradeProposalsPanel } from './TradeProposalsPanel';
import { ConversationHistoryRail } from './ConversationHistoryRail';

/**
 * AI assistant page — two-column layout: conversation history rail on the
 * left, current chat on the right. Selecting a row in the rail loads that
 * conversation into the panel; the "New" button starts a fresh one.
 *
 * The history rail refetches whenever:
 *  - the user picks a different conversation (so its own selection state syncs),
 *  - a turn completes that may have created/disabled a schedule, or
 *  - a brand-new conversation was just committed (its id arrived on `done`).
 */
export const AiAssistantPage: React.FC = () => {
  const [context, setContext] = useState('general');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [railRefreshKey, setRailRefreshKey] = useState(0);

  /** Context drives the system-prompt framing — switching contexts inside a
   *  selected conversation would mix two framings on one thread. Always
   *  start fresh when the user changes context. */
  const onContextChange = useCallback((next: string) => {
    setContext(next);
    setSelectedId(null);
  }, []);

  const onConversationChanged = useCallback((id: string | null) => {
    setSelectedId(id);
    setRailRefreshKey((k) => k + 1);
  }, []);

  const onTurnComplete = useCallback(() => {
    setRailRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="flex h-full">
      <ConversationHistoryRail
        selectedId={selectedId}
        onSelect={setSelectedId}
        refreshKey={railRefreshKey}
      />

      <div className="flex-1 flex flex-col min-w-0 p-4 gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">AI Assistant</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Context</span>
            <Select value={context} onValueChange={onContextChange}>
              <SelectTrigger className="h-8 w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="paper">Paper trading</SelectItem>
                <SelectItem value="live">Live trading</SelectItem>
                <SelectItem value="strategy">Strategies</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Pending trade proposals — renders nothing when there are none. */}
        <TradeProposalsPanel />

        {/* The chat. Keyed on (selectedId, context) so changing either picks
            up a clean state — switching context to 'strategy' should start
            a fresh thread, not append to the previous one. */}
        <div className="flex-1 min-h-0">
          <AiChatPanel
            key={`${selectedId ?? 'new'}|${context}`}
            context={context}
            conversationId={selectedId}
            onConversationChanged={onConversationChanged}
            onTurnComplete={onTurnComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPage;
